// --- Dependencies -------------------------------------------------------------------------------
import { useEffect } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyAdmin } from "@/types";
import { formatDate } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import StatusChip from "@/UI/atoms/StatusChip";
import FactsGrid, { type TyFact } from "@/UI/components/FactsGrid";
import RecordPanel from "@/UI/components/RecordPanel";
import PasswordFormPopup from "@/UI/popups/PasswordFormPopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
/**
 * The admin's own account. Every admin has one, so it carries no gate — it is reached from the account
 * chip rather than from the navigation, which is why it has no tab.
 *
 * Changing one's own password is the only thing that happens here. **Issuing** one is somebody else's
 * screen: an admin cannot reset their own, the same way a client cannot.
 */
export default function ProfileScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const admin = useAuth((state) => state.admin);
    const setAdmin = useAuth((state) => state.setAdmin);
    const addComp = usePopup((state) => state.addComp);

    /**
     * Re-read after a change rather than patching the stored copy by hand: `isPasswordTemporary`
     * flips as a side effect of the change, and the backend is the only thing that knows it did.
     */
    const session = useFetch<{ admin: TyAdmin }>("/auth/me", {}, false);

    useEffect(() => {
        if (session.data?.admin) setAdmin(session.data.admin);
    }, [session.data, setAdmin]);

    if (!admin) return <p className="screen-state">{t.common.loading}</p>;

    const facts: TyFact[] = [
        { key: "phone", label: t.admins.fields.phone, value: admin.phone, ltr: true },
        { key: "email", label: t.admins.fields.email, value: admin.email ?? "", ltr: true },
        { key: "createdAt", label: t.admins.fields.createdAt, value: formatDate(admin.createdAt, lang) },
    ].filter((fact) => fact.value);

    const openPasswordForm = () =>
        addComp({
            name: "password-form",
            Component: PasswordFormPopup,
            anim: "scale",
            details: { onDone: () => session.invoke() },
        });

    return (
        <section className="profile-screen">
            <RecordPanel
                avatar={<Avatar name={admin.name} picture={admin.picture} size={46} bordered />}
                title={admin.name}
                subtitle={admin.phone}
                chips={
                    <>
                        {admin.isSuper && <StatusChip size="sm" tone="accent" label={t.admins.super} />}
                        {admin.isPasswordTemporary && <StatusChip size="sm" tone="warn" label={t.password.temporary} />}
                    </>
                }
                primary={{ label: t.profile.changePassword, onPress: openPasswordForm }}
            />

            {/* The founding account is seeded with a temporary password, so this is the first thing the
                first admin ever sees — and the one screen that can do anything about it. */}
            {admin.isPasswordTemporary && <p className="profile-screen__note">{t.auth.temporaryPassword}</p>}

            <FactsGrid facts={facts} />
        </section>
    );
}
